import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TransferDTO, CreateTransferDTO, TransferState, TransferPaginationResponse } from '../../types/transfer';
import { createTransferAPI, deleteTransferAPI, fetchTransfersAPI, searchProductByCodeAPI } from './transferAPI';
import { RootState } from '../../app/store';

const initialState: TransferState = {
  items: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10,
  hasNext: false,
  hasPrev: false
};

export const fetchTransfers = createAsyncThunk(
  'transfer/fetchTransfers',
  async ({ page, limit, search, storeId, type, fromDate, toDate }: { page?: number; limit?: number; search?: string; storeId?: string; type?: string; fromDate?: string; toDate?: string }, { rejectWithValue }) => {
    try {
      const response = await fetchTransfersAPI(page, limit, search, storeId, type, fromDate, toDate);
      if (!response.status) {
        return rejectWithValue(response);
      }
      
      if (!response.data) {
        return rejectWithValue({ status: false, message: 'Failed to fetch transfers' });
      }
      
      // Return complete response with both data and pagination
      return { 
        response: { 
          data: response.data, 
          pagination: response.pagination 
        }, 
        requestedLimit: limit 
      }; 
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const searchProductByCode = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>('transfer/searchProductByCode', async (code, { rejectWithValue }) => {
  const result = await searchProductByCodeAPI(code);
  
  // Check if product was actually found
  // API returns: { status: true, data: { status: false, message: "Product not found" } } when not found
  if (result.status && result.data && result.data.status !== false) {
    // Product found - return the data
    return result;
  } else {
    // Product not found
    const errorMsg = result.data?.message || result.message || 'Product not found';
    return rejectWithValue(errorMsg);
  }
});

export const createTransfer = createAsyncThunk(
  'transfer/createTransfer',
  async (transfer: CreateTransferDTO, { rejectWithValue }) => {
    try {
      const response = await createTransferAPI(transfer);
      
      // Check if response indicates failure
      if (!response.status) {
        // Extract proper error message
        const errorMessage = response.message || 'Failed to create transfer';
        return rejectWithValue({ message: errorMessage });
      }
      
      return response.data;
    } catch (error: any) {
      // Handle unexpected errors
      const errorMessage = error?.message || error?.data?.message || 'Failed to create transfer';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

export const deleteTransfer = createAsyncThunk(
  'transfer/deleteTransfer',
  async (uuid: string, { rejectWithValue }) => {
    const response = await deleteTransferAPI(uuid);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return uuid;
  }
);

const transferSlice = createSlice({
  name: 'transfer',
  initialState,
  reducers: {
    resetTransfer: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalPages = 0;
      state.totalItems = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch transfers
      .addCase(fetchTransfers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = false;
          state.error = null;
          
          const { response, requestedLimit } = action.payload as any;
          
          if (response && response.data && Array.isArray(response.data) && response.pagination) {
            // New format: { data: [...], pagination: {...} }
            state.items = response.data;
            state.currentPage = response.pagination.currentPage;
            state.totalPages = response.pagination.totalPages;
            state.totalItems = response.pagination.totalItems;
            state.itemsPerPage = requestedLimit || response.pagination.pageSize;
            state.hasNext = response.pagination.hasNext;
            state.hasPrev = response.pagination.hasPrev;
          } else if (Array.isArray(response)) {
            // Fallback: direct array
            state.items = response;
            state.currentPage = 1;
            state.totalPages = 1;
            state.totalItems = response.length;
            state.itemsPerPage = requestedLimit || 10;
            state.hasNext = false;
            state.hasPrev = false;
          } else {
            state.items = [];
          }
        }
      })
      .addCase(fetchTransfers.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to fetch transfers';
        } else {
          state.error = action.error.message ?? 'Failed to fetch transfers';
        }
      })
      // Create transfer
      .addCase(createTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransfer.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createTransfer.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to create transfer';
        } else {
          state.error = action.error.message ?? 'Failed to create transfer';
        }
      })
      // Delete transfer
      .addCase(deleteTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransfer.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteTransfer.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to delete transfer';
        } else {
          state.error = action.error.message ?? 'Failed to delete transfer';
        }
      });
  },
});

export const { resetTransfer } = transferSlice.actions;

export const selectTransfers = (state: RootState) => state.transfer.items;
export const selectTransferLoading = (state: RootState) => state.transfer.loading;
export const selectTransferError = (state: RootState) => state.transfer.error;
export const selectTransferPagination = (state: RootState) => ({
  currentPage: state.transfer.currentPage,
  totalPages: state.transfer.totalPages,
  totalItems: state.transfer.totalItems,
  itemsPerPage: state.transfer.itemsPerPage,
  hasNext: state.transfer.hasNext,
  hasPrev: state.transfer.hasPrev,
});

export default transferSlice.reducer;
